import React, { useRef, useEffect, useState } from "react";
import { Task } from "../../types/public-types";
import { BarTask } from "../../types/bar-task";
import styles from "./tooltip.module.css";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NewPlus from "@/assets/svg/NewPlus.svg";
import Edit from "@/assets/svg/EditPen.svg";
import TrashCan from "@/assets/svg/TrashCan.svg";
import ColorPicker from "@/assets/svg/Colorpicker.svg";
export type ContextMenuProps = {
  task: BarTask;
  arrowIndent: number;
  rtl: boolean;
  svgContainerHeight: number;
  svgContainerWidth: number;
  svgWidth: number;
  headerHeight: number;
  taskListWidth: number;
  rowHeight: number;
  fontSize: string;
  fontFamily: string;
  remove: () => void;
  onColorChange: (Task: Task) => void;
  onEventCall: (data: { action: string; data: Task }) => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
  ContexContent?: React.FC<{
    task: Task;
    fontSize: string;
    fontFamily: string;
    userId?: string;
    onEventCall: (data: { action: string; data: Task }) => void;
    onColorChange: (data: Task) => void;
    closeMenu: () => void;
  }>;
};

export const ContextManu: React.FC<ContextMenuProps> = ({
  task,
  fontSize,
  fontFamily,
  remove,
  onColorChange,
  onEventCall,
  wrapperRef,
  ContexContent,
}) => {
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    if (wrapperRef && wrapperRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const newMouseX = event.clientX - wrapperRect.left + 350;
      const newMouseY = event.clientY - wrapperRect.top + 150;

      // Ensure the context menu stays within the screen bounds
      const contextMenuWidth = contextMenuRef.current?.offsetWidth || 0;
      const contextMenuHeight = contextMenuRef.current?.offsetHeight || 0;
      const maxX = wrapperRect.width - contextMenuWidth + 150;
      const maxY = wrapperRect.height - contextMenuHeight - 50;

      setMouseX(Math.min(newMouseX, maxX));
      setMouseY(Math.min(newMouseY, maxY));
    }
    setIsVisible(true);
  };

  const closeMenu = () => {
    setIsVisible(false);
    remove();
  };

  const handleClickOutside = (event: MouseEvent) => {
    console.log(event);
    
    if (
      contextMenuRef.current &&
      !contextMenuRef.current.contains(event.target as Node)
    ) {
      closeMenu();
    }
  };

  useEffect(() => {
    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const contextMenuStyle = {
    display: isVisible ? "block" : "none",
    left: `${mouseX}px`,
    top: `${mouseY}px`,
  };
  const style = {
    fontSize,
    fontFamily,
    left: `${mouseX - 340}px`,
    top: `${mouseY - 140}px`,
  };
  return (
    <div
      ref={contextMenuRef}
      className={styles.contextMenuContainer}
      style={contextMenuStyle}
    >
      {ContexContent ? (
        <div
          className={
            styles.contextMenuDefaultContainer +
            " absolute "
          }
          style={style}
        >
          <ContexContent task={task} fontSize={fontSize} fontFamily={fontFamily}  closeMenu={closeMenu} onEventCall={onEventCall} onColorChange={onColorChange}/>
        </div>
      ) : (
        <StandardMenuContent
          task={task}
          fontSize={fontSize}
          fontFamily={fontFamily}
          closeMenu={closeMenu}
          left={mouseX}
          top={mouseY}
          onColorChange={onColorChange}
          onEventCall={onEventCall}
        />
      )}
    </div>
  );
};

export const StandardMenuContent: React.FC<{
  task: Task;
  fontSize: string;
  fontFamily: string;
  closeMenu: () => void;
  left: number;
  top: number;
  onColorChange: (data: Task) => void;
  onEventCall: (data: { action: string; data: Task }) => void;
}> = ({
  task,
  fontSize,
  fontFamily,
  closeMenu,
  left,
  top,
  onColorChange,
  onEventCall,
}) => {
  const style = {
    fontSize,
    fontFamily,
    left: `${left - 340}px`,
    top: `${top - 140}px`,
  };
  const colors = [
    { id: "red", color: "#ef4444", title: "Red" },
    { id: "pink", color: "#ec4899", title: "Pink" },
    { id: "purple", color: "#a855f7", title: "Purple" },
    { id: "magenta", color: "#ff4dff", title: "Magenta" },
    { id: "violet", color: "#8b5cf6", title: "Violet" },
    { id: "indigo", color: "#6366f1", title: "Indigo" },
    { id: "blue", color: "#3b82f6", title: "Blue" },
    { id: "cyan", color: "#06b6d4", title: "Cyan" },
    { id: "teal", color: "#14b8a6", title: "Teal" },
    { id: "green", color: "#22c55e", title: "Green" },
    { id: "gantt-green", color: "#15803d", title: "Gantt-green" },
    { id: "lime", color: "#84cc16", title: "Lime" },
    { id: "yellow", color: "#eab308", title: "Yellow" },
    { id: "orange", color: "#f97316", title: "Orange" },
    { id: "deep-orange", color: "#ff7043", title: "Deep-orange" },
    { id: "gray", color: "#6b7280", title: "Gray" },
    { id: "light-gray", color: "#d1d5db", title: "Light-gray" },
    { id: "null", color: "#3b82f6", title: "No color", border: true },
  ];
  const [activeColor, setActiveColor] = useState("");
  const handleColorSelect = (color: string) => {
    setActiveColor(color);
    const findColor = colors.find((c) => c.id == color);
    onColorChange({
      ...task,
      styles: {
        backgroundColor: findColor?.color,
        backgroundSelectedColor: findColor?.color,
        progressColor: "#80808088",
        progressSelectedColor: "#80808088",
      },
    });
  };
  return (
    <div
      className={
        styles.contextMenuDefaultContainer +
        " absolute bg-white p-2 rounded-md shadow-md"
      }
      style={style}
    >
      <div
        className={
          styles.contextMenuList +
          " flex flex-col gap-3 items-center justify-center opacity-80 font-semibold text-gray-700"
        }
        autoFocus
        onBlur={closeMenu}
      >
        <div
          className="flex gap-2 items-center w-full cursor-pointer"
          onClick={() => onEventCall({ action: "createSubTask", data: task })}
        >
          <img src={NewPlus} className={"invert !w-4 !min-w-4"} /> SubTask
        </div>
        <div
          className="flex gap-2 items-center w-full cursor-pointer"
          onClick={() => onEventCall({ action: "edit", data: task })}
        >
          {" "}
          <img src={Edit} className={" w-4 min-w-4 invert"} />
          Edit
        </div>
        <div
          className="flex gap-2 items-center w-full cursor-pointer"
          onClick={() => onEventCall({ action: "delete", data: task })}
        >
          <img src={TrashCan} className={" w-4 min-w-4"} />
          Delete Task
        </div>

        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger className="flex gap-1 w-full items-center bg-white">
              <div className="flex gap-2 items-center w-full   ">
                <img src={ColorPicker} className={" w-4 min-w-4 opacity-80"} />
                Color
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="relative overflow-hidden bg-white rounded-md w-64 h-fit ">
                <ul
                  className="grid grid-cols-6 gap-2 p-2 overflow-auto"
                  role="listbox"
                  aria-activedescendant="active-color"
                >
                  {colors.map((color) => (
                    <li
                      onClick={() => handleColorSelect(color.id)}
                      key={color.id}
                      className={`flex items-center justify-center w-8 h-8 cursor-pointer rounded-full material-icons ${
                        color.border ? "border border-gray-300" : ""
                      }`}
                      style={{
                        backgroundColor: color.id !== "null" ? color.color : "",
                      }}
                      role="option"
                      aria-selected={
                        color.id === activeColor ? "true" : "false"
                      }
                      data-id={color.id}
                      title={color.title}
                      id={color.id === activeColor ? "active-color" : undefined}
                    >
                      {color.id == "null" ? <i></i> : ""}
                    </li>
                  ))}
                </ul>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
